import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { mmYyToDate, normalizeToMmYy, pad2 } from "@/lib/progressRange";
import { ChevronDown } from "lucide-react";

type MonthYearPickerProps = {
  value?: string; // mm-yy
  min?: string; // mm-yy
  max?: string; // mm-yy
  disabled?: boolean;
  onChange: (nextMmYy: string) => void;
  triggerClassName?: string;
  triggerLabel?: string;
};

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] as const;

const formatMmYyHuman = (mmYy: string) => {
  const d = mmYyToDate(mmYy);
  if (Number.isNaN(d.getTime())) return mmYy;
  const m = MONTHS_SHORT[d.getUTCMonth()];
  const y = d.getUTCFullYear();
  return `${m} ${y}`;
};

const inBounds = (mmYy: string, min?: string, max?: string) => {
  const t = mmYyToDate(mmYy).getTime();
  if (!Number.isFinite(t)) return false;
  if (min) {
    const minT = mmYyToDate(min).getTime();
    if (Number.isFinite(minT) && t < minT) return false;
  }
  if (max) {
    const maxT = mmYyToDate(max).getTime();
    if (Number.isFinite(maxT) && t > maxT) return false;
  }
  return true;
};

export const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  value,
  min,
  max,
  disabled,
  onChange,
  triggerClassName,
  triggerLabel,
}) => {
  const normalizedValue = normalizeToMmYy(value ?? "") ?? undefined;
  const normalizedMin = normalizeToMmYy(min ?? "") ?? undefined;
  const normalizedMax = normalizeToMmYy(max ?? "") ?? undefined;

  const years = useMemo(() => {
    const minD = normalizedMin ? mmYyToDate(normalizedMin) : undefined;
    const maxD = normalizedMax ? mmYyToDate(normalizedMax) : undefined;
    if (!minD || !maxD || Number.isNaN(minD.getTime()) || Number.isNaN(maxD.getTime())) return [];
    const out: number[] = [];
    for (let y = minD.getUTCFullYear(); y <= maxD.getUTCFullYear(); y++) out.push(y);
    return out;
  }, [normalizedMax, normalizedMin]);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"year" | "month">("year");
  const [activeYear, setActiveYear] = useState<number | null>(null);

  const effectiveLabel = triggerLabel ?? (normalizedValue ? formatMmYyHuman(normalizedValue) : "Select");

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) {
          setStep("year");
          setActiveYear(null);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "text-xs md:text-sm text-vb-text-3 transition-colors inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed min-h-10 sm:min-h-0",
            !disabled && "hover:text-vb-text",
            triggerClassName,
          )}
        >
          <span>{effectiveLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={8}
        className="w-[280px] rounded-vb-lg bg-vb-surface p-1.5 shadow-vb-xl border border-vb-line-strong"
      >
        {step === "year" ? (
          <div className="flex flex-col gap-1">
            {years.map((y) => {
              const isSelectedYear =
                (activeYear ?? null) === y ||
                (!activeYear && normalizedValue && mmYyToDate(normalizedValue).getUTCFullYear() === y);
              return (
                <button
                  key={y}
                  type="button"
                  className={cn(
                    "px-3 py-2 rounded-vb-md text-left text-vb-text hover:bg-vb-brand-soft transition-colors min-h-10 sm:min-h-0",
                    isSelectedYear && "bg-vb-brand text-vb-on-brand hover:bg-vb-brand",
                  )}
                  onClick={() => {
                    setActiveYear(y);
                    setStep("month");
                  }}
                >
                  {y}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="text-vb-text hover:bg-vb-brand-soft px-2 py-1 rounded-vb-md transition-colors"
                onClick={() => setStep("year")}
              >
                Year
              </button>
              <div className="text-sm font-medium text-vb-text">{activeYear}</div>
              <div className="w-10" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MONTHS_SHORT.map((m, idx) => {
                const yy = String(activeYear ?? "").slice(-2);
                const mmYy = `${pad2(idx + 1)}-${yy}`;
                const enabled = !!activeYear && inBounds(mmYy, normalizedMin, normalizedMax);
                const isSelected = normalizedValue === mmYy;
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={!enabled}
                    className={cn(
                      "px-2 py-2 rounded-vb-md text-vb-text hover:bg-vb-brand-soft transition-colors disabled:opacity-40 disabled:cursor-not-allowed min-h-10 sm:min-h-0",
                      isSelected && "bg-vb-brand text-vb-on-brand hover:bg-vb-brand",
                    )}
                    onClick={() => {
                      if (!enabled) return;
                      onChange(mmYy);
                      setOpen(false);
                    }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

